// Share codes (spec §11): a prefix + Crockford base32 body, most significant
// bit first. Two formats exist:
//
// `AOD2-` — the current format (the preset-index widening; see below). The
// encoder always writes it. Payload, most significant first:
//
//   bits  field
//   4     ruleset contract version (Ruleset.v, 1)
//   1     customised flag (ruleset.preset === 'custom')
//   5     base preset index in PRESETS (0 explorer …; room for 32)
//   6     module count n (MODULE_IDS.length when encoded; 43 today)
//   n     module bits, bit i = MODULE_IDS[i]
//   2     pace              relaxed | normal | brisk
//   2     mapSize           tiny | small | medium | large
//   3     aiOpponents       1–7
//   2     startingResources low | normal | high
//   2     victoryLength     short | normal | long
//   6     peacePeriodMin    in half minutes, 0–40
//   1     cityCanBeDestroyed
//   4     truceMin          in half minutes, 0–10
//   2     intensityCeiling  low | medium | high | off
//   2     content           family | teen | mature
//   2     climate           temperate | arctic | tropical | desert
//   2     thirst            auto | on | off
//   2     sea               rim | landlocked | open | archipelago
//   2     reserved          earmarked for Ruleset.piracy — must read 0
//   8     CRC-8 (poly 0x07) over the bytes before it
//
// `AOD1-` — the legacy format, decoded forever, never written again: the same
// layout with a **3-bit** preset index and the climate byte optional (codes
// from before module #33 lack it and decode as temperate / auto / rim). See
// the decoder's legacy branch for that layout.
//
// The module count makes the code additive-safe: MODULE_IDS only ever grows,
// so a code from an older build decodes with the newer modules off. A longer
// module list grows the payload (and the code) without a format change.
// Preset indices are append-only for the same reason: the code stores a
// position in PRESETS, so a new preset appends — inserting one earlier would
// silently rebind every existing code.
//
// Widening needed a new prefix rather than a quiet extra bit: the two AOD1
// layouts are told apart by total bit length alone, so a header change under
// the old prefix would have misdecoded every existing code. The decoder
// already answered an unknown `AOD<n>-` with "newer game, not a typo" —
// `AOD2` is that future arriving.
//
// Decoding accepts lower case, spaces and dashes, and Crockford's I/L → 1,
// O → 0 look-alikes. The seed is not included (spec: only for "same map").

import {
  type Climate,
  type ContentRating,
  MODULE_IDS,
  type ModuleId,
  PRESETS,
  type Ruleset,
  type Sea,
  type Thirst,
  type Tuning,
  validateRuleset,
} from '@aod/protocol';
import type { PresetDefId } from './setupModel.js';

/** The legacy format — decoded forever, never written again. */
export const SHARE_CODE_PREFIX = 'AOD1-';
/** The current format — the only one the encoder writes. */
export const SHARE_CODE_PREFIX_V2 = 'AOD2-';
/** The `AOD2` payload plus the calendar axis (generations §9.2): written
 *  only when the year length is not the default 6, so classic codes still
 *  cross versions. */
export const SHARE_CODE_PREFIX_V3 = 'AOD3-';

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

const PACE: readonly Tuning['pace'][] = ['relaxed', 'normal', 'brisk'];
const MAP_SIZE: readonly Tuning['mapSize'][] = ['tiny', 'small', 'medium', 'large'];
const RESOURCES: readonly Tuning['startingResources'][] = ['low', 'normal', 'high'];
const VICTORY_LENGTH: readonly Tuning['victoryLength'][] = ['short', 'normal', 'long'];
const INTENSITY: readonly Tuning['intensityCeiling'][] = ['low', 'medium', 'high', 'off'];
const CONTENT: readonly ContentRating[] = ['family', 'teen', 'mature'];
const CLIMATES: readonly Climate[] = ['temperate', 'arctic', 'tropical', 'desert'];
const THIRSTS: readonly Thirst[] = ['auto', 'on', 'off'];
const SEAS: readonly Sea[] = ['rim', 'landlocked', 'open', 'archipelago'];

export interface SharedRuleset {
  readonly ruleset: Ruleset;
  /** The preset the ruleset was built from (equals ruleset.preset unless custom). */
  readonly basedOn: PresetDefId;
}

export type DecodeResult =
  | { readonly ok: true; readonly value: SharedRuleset }
  | { readonly ok: false; readonly error: ShareCodeError; readonly problems?: readonly string[] };

export type ShareCodeError = 'format' | 'checksum' | 'version' | 'value' | 'invalid';

// ── Bit packing ────────────────────────────────────────────────────────

class BitWriter {
  private readonly bits: number[] = [];
  write(value: number, width: number): void {
    for (let i = width - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
  bytes(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let b = 0;
      for (let j = 0; j < 8; j++) b = (b << 1) | (this.bits[i + j] ?? 0);
      out.push(b);
    }
    return out;
  }
}

class BitReader {
  private pos = 0;
  constructor(private readonly bits: readonly number[]) {}
  read(width: number): number {
    let v = 0;
    for (let i = 0; i < width; i++) v = v * 2 + (this.bits[this.pos++] ?? 0);
    return v;
  }
  get remaining(): number {
    return this.bits.length - this.pos;
  }
}

function crc8(bytes: readonly number[]): number {
  let crc = 0;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
  }
  return crc;
}

function toBase32(bytes: readonly number[]): string {
  let out = '';
  let acc = 0;
  let n = 0;
  for (const b of bytes) {
    acc = (acc << 8) | b;
    n += 8;
    while (n >= 5) {
      out += ALPHABET[(acc >>> (n - 5)) & 31];
      n -= 5;
    }
    acc &= (1 << n) - 1;
  }
  if (n > 0) out += ALPHABET[(acc << (5 - n)) & 31];
  return out;
}

function fromBase32(text: string): number[] | null {
  const bits: number[] = [];
  for (const ch of text) {
    const v = ALPHABET.indexOf(ch);
    if (v < 0) return null;
    for (let i = 4; i >= 0; i--) bits.push((v >>> i) & 1);
  }
  return bits;
}

// ── Encode ─────────────────────────────────────────────────────────────

const half = (min: number) => Math.round(min * 2);

function indexIn<T>(list: readonly T[], value: T): number {
  const i = list.indexOf(value);
  if (i < 0) throw new Error(`share code: unsupported value ${String(value)}`);
  return i;
}

/** Encode a ruleset (and the preset it's based on) as an `AOD2-…` code. */
export function encodeShareCode(ruleset: Ruleset, basedOn: PresetDefId): string {
  const w = new BitWriter();
  const t = ruleset.tuning;
  w.write(ruleset.v, 4);
  w.write(ruleset.preset === 'custom' ? 1 : 0, 1);
  w.write(
    indexIn(
      PRESETS.map((p) => p.id),
      basedOn,
    ),
    5,
  );
  w.write(MODULE_IDS.length, 6);
  const on = new Set(ruleset.modules);
  for (const m of MODULE_IDS) w.write(on.has(m) ? 1 : 0, 1);
  w.write(indexIn(PACE, t.pace), 2);
  w.write(indexIn(MAP_SIZE, t.mapSize), 2);
  w.write(Math.max(0, Math.min(7, Math.round(t.aiOpponents))), 3);
  w.write(indexIn(RESOURCES, t.startingResources), 2);
  w.write(indexIn(VICTORY_LENGTH, t.victoryLength), 2);
  w.write(Math.max(0, Math.min(40, half(t.peacePeriodMin))), 6);
  w.write(t.cityCanBeDestroyed ? 1 : 0, 1);
  w.write(Math.max(0, Math.min(10, half(t.truceMin))), 4);
  w.write(indexIn(INTENSITY, t.intensityCeiling), 2);
  w.write(indexIn(CONTENT, ruleset.content), 2);
  // Climate + thirst + sea + 2 reserved: a whole byte, so the layout is always
  // strictly longer than the pre-climate one (never length-ambiguous).
  w.write(indexIn(CLIMATES, ruleset.climate ?? 'temperate'), 2);
  w.write(indexIn(THIRSTS, ruleset.thirst ?? 'auto'), 2);
  // The sea pair ships writer + reader together (type Sea, these bits, the
  // decoder below — one PR): a payload change without a paired reader is how
  // a share code quietly stops meaning what it meant (the pin rule applied
  // to codes).
  w.write(indexIn(SEAS, ruleset.sea ?? 'rim'), 2);
  // Trailing 2 bits are earmarked for Ruleset.piracy (sea-design §5.3) and
  // must stay ZERO until that field ships WITH its reader — never write a
  // value the current decoders ignore.
  w.write(0, 2);
  // The calendar axis (generations §9.2, decided): a non-default year
  // length ships as an `AOD3` code with 5 extra bits at the tail — writer
  // and reader in this same PR, older decoders answer "newer game". The
  // default 6 keeps the `AOD2` layout byte-for-byte, so classic codes
  // still cross versions.
  const yearIdx =
    ruleset.yearMinutes !== undefined && ruleset.yearMinutes !== 6
      ? Math.round((ruleset.yearMinutes - 2) * 2)
      : null;
  if (yearIdx !== null) w.write(Math.max(0, Math.min(20, yearIdx)), 5);
  const bytes = w.bytes();
  bytes.push(crc8(bytes));
  return (yearIdx !== null ? SHARE_CODE_PREFIX_V3 : SHARE_CODE_PREFIX_V2) + toBase32(bytes);
}

// ── Decode ─────────────────────────────────────────────────────────────

/** Upper-case, drop spaces/dashes and map Crockford look-alikes. */
function normaliseInput(input: string): string {
  return input.toUpperCase().replace(/[\s-]/g, '').replace(/[IL]/g, '1').replace(/O/g, '0');
}

function pick<T>(list: readonly T[], i: number): T | undefined {
  return list[i];
}

/**
 * The tuning block onward is bit-identical in both formats, so both decoders
 * share this tail. `hasClimate` is false only for pre-climate AOD1 codes,
 * which end before the climate byte and decode temperate / auto / rim.
 */
function finishDecode(
  r: BitReader,
  custom: boolean,
  basedOn: PresetDefId | undefined,
  hasClimate: boolean,
  modules: ModuleId[],
  hasYear: boolean,
): DecodeResult {
  const pace = pick(PACE, r.read(2));
  const mapSize = pick(MAP_SIZE, r.read(2));
  const aiOpponents = r.read(3);
  const startingResources = pick(RESOURCES, r.read(2));
  const victoryLength = pick(VICTORY_LENGTH, r.read(2));
  const peacePeriodMin = r.read(6) / 2;
  const cityCanBeDestroyed = r.read(1) === 1;
  const truceMin = r.read(4) / 2;
  const intensityCeiling = pick(INTENSITY, r.read(2));
  const content = pick(CONTENT, r.read(2));
  const climate = hasClimate ? pick(CLIMATES, r.read(2)) : 'temperate';
  // Codes from before thirst wrote 0 here, which is 'auto'.
  const thirst = hasClimate ? pick(THIRSTS, r.read(2)) : 'auto';
  // Same gate as thirst: pre-climate codes end before these bits.
  const sea = hasClimate ? pick(SEAS, r.read(2)) : 'rim';
  // The piracy earmark must still be zero — a non-zero value came from a
  // future client whose semantics we do not have (pair rule, encoder side).
  if (hasClimate && r.read(2) !== 0) return { ok: false, error: 'version' };
  // The calendar axis (generations §9.2), only on `AOD3`: 5 bits, 21
  // half-minute steps from 2 to 12. An index past the table came from a
  // future client — a version boundary, not a value.
  let yearMinutes: number | undefined;
  if (hasYear) {
    const idx = r.read(5);
    if (idx > 20) return { ok: false, error: 'version' };
    yearMinutes = 2 + idx * 0.5;
  }
  if (
    !pace ||
    !mapSize ||
    !startingResources ||
    !victoryLength ||
    !intensityCeiling ||
    !content ||
    !thirst ||
    !basedOn
  ) {
    return { ok: false, error: 'value' };
  }

  const ruleset: Ruleset = {
    v: 1,
    preset: custom ? 'custom' : basedOn,
    modules,
    tuning: {
      pace,
      mapSize,
      aiOpponents,
      startingResources,
      victoryLength,
      peacePeriodMin,
      cityCanBeDestroyed,
      truceMin,
      intensityCeiling,
    },
    content,
    ...(climate !== undefined && climate !== 'temperate' && { climate }),
    ...(thirst !== 'auto' && { thirst }),
    ...(sea !== 'rim' && { sea }),
    ...(yearMinutes !== undefined && yearMinutes !== 6 && { yearMinutes }),
  };
  const problems = validateRuleset(ruleset);
  if (problems.length > 0) return { ok: false, error: 'invalid', problems };
  return { ok: true, value: { ruleset, basedOn } };
}

/** Parse an `AOD1-…`, `AOD2-…` or `AOD3-…` code back into a ruleset; never throws. */
export function decodeShareCode(input: string): DecodeResult {
  const text = normaliseInput(input);
  const legacyPrefix = normaliseInput(SHARE_CODE_PREFIX);
  const modernPrefix = normaliseInput(SHARE_CODE_PREFIX_V2);
  const v3Prefix = normaliseInput(SHARE_CODE_PREFIX_V3);
  const legacy = text.startsWith(legacyPrefix);
  const v3 = !legacy && text.startsWith(v3Prefix);
  const modern = !legacy && !v3 && text.startsWith(modernPrefix);
  if (!legacy && !modern && !v3) {
    // A later format (AOD4-…) is a newer game, not a typo.
    return { ok: false, error: /^\s*AOD\d+\s*-/i.test(input) ? 'version' : 'format' };
  }
  const bits = fromBase32(
    text.slice((legacy ? legacyPrefix : v3 ? v3Prefix : modernPrefix).length),
  );
  if (!bits || bits.length < 20) return { ok: false, error: 'format' };

  const header = new BitReader(bits);
  const v = header.read(4);
  header.read(1);
  // AOD1 carries a 3-bit preset index; AOD2 widened it to 5 (append-only
  // positions — a new preset appends, it never shifts the existing ones).
  header.read(legacy ? 3 : 5);
  const moduleCount = header.read(6);
  // AOD1 has two layouts: pre-climate codes (no climate field) and later
  // ones (+2 bits). AOD2 always carries the climate byte — one layout.
  // AOD3 is that layout plus 5 tail bits: the calendar axis.
  const headerBits = 4 + 1 + (legacy ? 3 : 5) + 6;
  const baseDataBits = headerBits + moduleCount + 24 + 2;
  const dataBits = baseDataBits + 8 + (v3 ? 5 : 0);
  const byteCount = Math.ceil(dataBits / 8);
  const expectedLengths = (legacy ? [baseDataBits, dataBits] : [dataBits]).map(
    (b) => Math.ceil(((Math.ceil(b / 8) + 1) * 8) / 5) * 5,
  );
  if (!expectedLengths.includes(bits.length)) {
    return { ok: false, error: 'format' };
  }
  const hasClimate = legacy ? bits.length === expectedLengths[1] : true;

  const bytes: number[] = [];
  const byteReader = new BitReader(bits);
  for (let i = 0; i < byteCount + 1; i++) bytes.push(byteReader.read(8));
  const crc = bytes.pop();
  if (crc !== crc8(bytes)) return { ok: false, error: 'checksum' };
  // Checked after the CRC so a typo reads as a typo, not as "newer version".
  if (v !== 1) return { ok: false, error: 'version' };

  const r = new BitReader(bits);
  r.read(4);
  const custom = r.read(1) === 1;
  const presetIndex = r.read(legacy ? 3 : 5);
  r.read(6);
  const modules: ModuleId[] = [];
  for (let i = 0; i < moduleCount; i++) {
    const on = r.read(1) === 1;
    const id = MODULE_IDS[i];
    // A module this build doesn't know yet can't be honoured.
    if (on && !id) return { ok: false, error: 'version' };
    if (on && id) modules.push(id);
  }
  const basedOn = PRESETS[presetIndex]?.id;
  if (!legacy && !basedOn) {
    // A valid checksum past the end of this build's preset list came from a
    // newer game — that is a version boundary, not a typo. (AOD1's 3 bits
    // top out at index 7, which is inside the nine-preset list, so a legacy
    // code can never name a preset that does not exist.)
    return { ok: false, error: 'version' };
  }
  const result = finishDecode(r, custom, basedOn, hasClimate, modules, v3);
  if (!result.ok) return result;
  return result;
}
